import React, { useCallback, useContext, useMemo, useState, useRef } from "react";
import { string, func, node, arrayOf, oneOfType, bool } from "prop-types";
import FormField from "@insightfulscience/atomic-react/FormField";
import ForwardRefPropType from "@insightfulscience/atomic-react/utils/forward-ref";
import { isString } from "@insightfulscience/atomic-react/utils/predicates";
import { classes } from "@insightfulscience/atomic-react/utils/classes";
import FormContext from "./context";
import { handleWith, composeRefs, asyncHandleWith } from "../events/handlers";
import renderError from "../renders/renderError";

const MemoizedFormField = React.memo(FormField);

function Field({
	name,
	parseEvent,
	onChange,
	onBlur,
	forwardRef,
	label,
	hint,
	error,
	setState,
	animate = false,
	fieldClassName = "",
	...props
}) {
	const [isFocus, setFocus] = useState(false);
	const { form, register, update, touch, errors, textRender, options } = useContext(FormContext);
	const { blurDelay } = options;
	const fieldRef = useRef(null);
	const value = useMemo(() => {
		return form[name];
	}, [form]);

	const blurAsyncComposer = useMemo(
		() => (blurDelay != null ? asyncHandleWith.m(blurDelay) : handleWith.m),
		[blurDelay],
	);

	const blurHandler = handleWith.m(onBlur, touch(name));
	const blurHandlerAsync = blurAsyncComposer(onBlur, touch(name));

	const handleBlur = useCallback(
		event => {
			setFocus(false);
			return event.relatedTarget != null ? blurHandlerAsync(event) : blurHandler(event);
		},
		[blurHandlerAsync, blurHandler],
	);

	const handleFocus = useCallback(() => {
		setFocus(true);
	}, []);

	const isError = useMemo(() => {
		return error || errors[name];
	}, [error, errors]);

	const fieldAnimateClassName = useMemo(() => {
		return [
			animate && "field-animate",
			animate && (isFocus || value) && "field-animate--focused",
			animate && isError && "field-animate--error",
		];
	}, [value, isError, isFocus]);

	const handleLabelClick = useCallback(() => {
		if (!isFocus) {
			fieldRef.current.focus();
			setFocus(true);
		}
	}, [isFocus, setFocus, form]);

	return (
		<MemoizedFormField
			{...props}
			fieldClassName={classes(...fieldAnimateClassName, fieldClassName)}
			name={name}
			hint={isString(hint) ? textRender(hint) : hint}
			label={isString(label) ? textRender(label) : label}
			value={form[name]}
			onChange={handleWith.m(onChange, update(setState || name, parseEvent))}
			onFocus={handleFocus}
			onBlur={handleBlur}
			forwardRef={composeRefs.m(forwardRef, register(name), fieldRef)}
			error={renderError(error || errors[name], textRender)}
			onLabelClick={handleLabelClick}
		/>
	);
}

Field.propTypes = {
	name: string.isRequired,
	fieldClassName: string,
	setState: func,
	parseEvent: func,
	onChange: func,
	onBlur: func,
	forwardRef: ForwardRefPropType,
	label: node,
	hint: node,
	animate: bool,
	error: oneOfType([string, arrayOf(string)]),
};
export default React.memo(Field);
